import React from 'react';
import { Box, TableCell } from '@mui/material';
import type { Header } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { useLocation } from 'react-router-dom';
import get from 'lodash/get';
import TableColumnResizer from '../column/TableColumnResizer';
import TableColumnSorter from '../column/TableColumnSorter';
import { Show } from '../../../../Show';
import { useQueryParams } from '../../../../providers/QueryParamsProvider';

const TableHeaderCell = ({ header }: TableHeaderCellProps) => {
  const { pathname } = useLocation();
  const { query, replace } = useQueryParams();
  const [isHovered, setIsHovered] = React.useState(false);
  const justifyContent = get(
    header,
    'column.columnDef.meta.headerStyle.justifyContent',
    'flex-start'
  );

  const handleSort = (
    e: React.MouseEvent<HTMLTableCellElement, MouseEvent>
  ) => {
    if (header.column.getIsSorted() && !header.column.getNextSortingOrder()) {
      const { order, orderBy, ...rest } = query;
      replace(pathname, {
        query: rest,
      });
    }

    header.column.getToggleSortingHandler()?.(e);
  };

  React.useEffect(() => {
    if (!header.column.getIsSorted()) {
      return;
    }

    replace(pathname, {
      query: {
        ...query,
        order: header.column.getIsSorted(),
        orderBy: header.column.id,
      },
    });
  }, [header.column.id, header.column.getIsSorted()]);

  return (
    <TableCell
      sx={{
        backgroundColor: (theme) => theme.serviceTable.headerCell.bgColor,
        fontFamily: 'Roboto',
        fontSize: '14px',
        fontWeight: '600',
        lineHeight: '20px',
        color: (theme) => theme.serviceTable.headerCell.color,
        width: header.getSize(),
        minWidth: header.getSize(),
        position: 'relative',
        padding: '1rem 1.333rem',
        borderBottom: (theme) =>
          `1px solid ${theme.serviceTable.headerCell.borderColor}`,
        ...(header.column.getCanSort() && { cursor: 'pointer' }),
      }}
      onClick={handleSort}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {header.isPlaceholder ? null : (
        <Box
          display="flex"
          gap="0.5rem"
          alignItems="center"
          justifyContent={justifyContent}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}

          <Show when={header.column.getCanSort()}>
            <TableColumnSorter header={header} />
          </Show>
        </Box>
      )}

      {(isHovered || header.column.getIsResizing()) && (
        <TableColumnResizer header={header} />
      )}
    </TableCell>
  );
};

export default TableHeaderCell;

type TableHeaderCellProps = {
  header: Header<unknown, unknown>;
};
